import React, { useEffect, useState, useRef, useCallback } from 'react';
import { normalizeContactData } from '../utils/contactDataUtils';
import { contactApi } from '../services';
import logoService from '../services/logoService';
import Icon from './Icon';
import ProfilePdfUpload from './ProfilePdfUpload';
import ContactHistory from './ContactHistory';
import ResearchButton from './ResearchButton';
import { contactAdvisors, getAdvisorColor, industryLabel, targetAccountLabel, OUTREACH_STAGE_LABELS } from './utils';
import './ContactDetailsDialog.css';

// "March 2022 – Present · 2 years 8 months"
const experienceDates = (exp) => {
  const range = exp.start_date || exp.end_date
    ? `${exp.start_date || 'Start date unknown'} – ${exp.end_date || 'Present'}`
    : null;
  return [range, exp.duration].filter(Boolean).join(' · ') || 'Duration Not Specified';
};

const EditorButtons = ({ onSave, onCancel, isUpdating, canSave }) => (
  <div className="inline-editor-buttons">
    <button
      type="button"
      onClick={onSave}
      disabled={isUpdating || !canSave}
      className="inline-editor-save-btn"
      aria-label={isUpdating ? 'Saving' : 'Save'}
    >
      <Icon name={isUpdating ? 'loader' : 'check'} spin={isUpdating} />
    </button>
    <button
      type="button"
      onClick={onCancel}
      disabled={isUpdating}
      className="inline-editor-cancel-btn"
      aria-label="Cancel"
    >
      <Icon name="x" />
    </button>
  </div>
);

const EditorError = ({ error }) => error ? (
  <div className="inline-editor-error" role="alert">
    <Icon name="alert-triangle" /> {error}
  </div>
) : null;

// Inline editing components - defined outside to prevent recreation
const InlineEmailEditor = ({ editFormData, setEditFormData, saveChanges, cancelEditing, isUpdating, error }) => (
  <div className="inline-editor">
    <div className="inline-editor-field">
      <label htmlFor="contact-email-input"><Icon name="mail" /> <strong>Email:</strong></label>
      <div className="inline-editor-input-group">
        <input
          id="contact-email-input"
          type="email"
          value={editFormData.email}
          onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
          className="inline-editor-input"
          placeholder="Enter email address"
          disabled={isUpdating}
          autoFocus
        />
        <EditorButtons onSave={saveChanges} onCancel={cancelEditing} isUpdating={isUpdating} canSave />
      </div>
      <EditorError error={error} />
    </div>
  </div>
);

const InlinePhoneEditor = ({ editFormData, setEditFormData, saveChanges, cancelEditing, isUpdating, error }) => (
  <div className="inline-editor">
    <div className="inline-editor-field">
      <label htmlFor="contact-phone-input"><Icon name="phone" /> <strong>Phone:</strong></label>
      <div className="inline-editor-input-group">
        <input
          id="contact-phone-input"
          type="tel"
          value={editFormData.phone}
          onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
          className="inline-editor-input"
          placeholder="Enter phone number"
          disabled={isUpdating}
          autoFocus
        />
        <EditorButtons onSave={saveChanges} onCancel={cancelEditing} isUpdating={isUpdating} canSave />
      </div>
      <EditorError error={error} />
    </div>
  </div>
);

const InlineLocationEditor = ({
  editFormData,
  setEditFormData,
  saveChanges,
  cancelEditing,
  isUpdating,
  error,
  locationSuggestions,
  isSearchingLocations,
  onLocationSearchChange,
  onLocationSuggestionSelect
}) => {
  const city = editFormData.city.trim();
  const country = editFormData.country.trim();
  return (
    <div className="inline-editor">
      <div className="inline-editor-field">
        <span><Icon name="map-pin" /> <strong>Location:</strong></span>
        <div className="inline-editor-location-group">
          <div className="inline-editor-location-inputs">
            <div className="location-input-container">
              <input
                type="text"
                value={editFormData.city}
                onChange={(e) => onLocationSearchChange(e.target.value, 'city')}
                className="inline-editor-input location-input"
                placeholder="City"
                aria-label="City"
                disabled={isUpdating}
                autoFocus
              />
              {isSearchingLocations && (
                <div className="location-loading">
                  <Icon name="loader" spin /> <span>Searching locations...</span>
                </div>
              )}
              {locationSuggestions.length > 0 && (
                <ul className="location-suggestions">
                  {locationSuggestions.map((location) => (
                    <li key={location.display_name}>
                      <button
                        type="button"
                        className="location-suggestion"
                        onClick={() => onLocationSuggestionSelect(location)}
                      >
                        {location.display_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <input
              type="text"
              value={editFormData.country}
              onChange={(e) => setEditFormData(prev => ({ ...prev, country: e.target.value }))}
              className="inline-editor-input location-input"
              placeholder="Country"
              aria-label="Country"
              disabled={isUpdating}
            />
          </div>
          {/* Both fields to set a location, or both empty to clear it */}
          <EditorButtons
            onSave={saveChanges}
            onCancel={cancelEditing}
            isUpdating={isUpdating}
            canSave={Boolean(city) === Boolean(country)}
          />
        </div>
        <EditorError error={error} />
      </div>
    </div>
  );
};

// Defined outside the dialog so re-renders keep the same element (and keyboard focus)
const EditButton = ({ label, onEdit }) => (
  <button
    type="button"
    className="edit-field-btn"
    onClick={(e) => {
      e.stopPropagation();
      onEdit();
    }}
    aria-label={`Edit ${label}`}
    title={`Edit ${label}`}
  >
    <Icon name="pencil" />
  </button>
);

const CollapsibleSection = ({ sectionKey, title, icon, expanded, onToggle, children }) => {
  const contentId = `contact-section-${sectionKey}`;
  return (
    <div className="collapsible-section">
      <div
        className="collapsible-section-header"
        onClick={() => onToggle(sectionKey)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={contentId}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(sectionKey);
          }
        }}
      >
        <h4 className="contact-dialog-section-title">
          <Icon name={icon} className="section-title-icon" /> {title}
        </h4>
        <span className={`section-toggle-icon ${expanded ? 'expanded' : 'collapsed'}`}>
          <Icon name="chevron-down" />
        </span>
      </div>
      {expanded && (
        <div className="collapsible-section-content" id={contentId}>
          {children}
        </div>
      )}
    </div>
  );
};

const sampleColorFromImage = (imgElement) => {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = imgElement.naturalWidth || imgElement.width;
      canvas.height = imgElement.naturalHeight || imgElement.height;

      ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const colors = [];
      const sampleSize = 20;

      for (let i = 0; i < sampleSize; i++) {
        const x = Math.floor(Math.random() * canvas.width);
        const y = Math.floor(Math.random() * canvas.height);
        const index = (y * canvas.width + x) * 4;

        if (index < data.length) {
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const a = data[index + 3];

          const brightness = (r + g + b) / 3;
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;

          if (a > 200 && brightness > 40 && brightness < 200 && saturation > 0.3) {
            colors.push({ r, g, b, brightness, saturation });
          }
        }
      }

      if (colors.length === 0) {
        resolve('#4a90e2');
        return;
      }

      let bestColor = colors[0];
      let bestScore = 0;

      colors.forEach(color => {
        const brightnessScore = Math.max(0, 1 - (color.brightness - 80) / 120);
        const score = color.saturation * brightnessScore;

        if (score > bestScore) {
          bestScore = score;
          bestColor = color;
        }
      });

      const hex = `#${bestColor.r.toString(16).padStart(2, '0')}${bestColor.g.toString(16).padStart(2, '0')}${bestColor.b.toString(16).padStart(2, '0')}`;
      resolve(hex);
    } catch (error) {
      // Cross-origin logos cannot be sampled; keep the default colour
      resolve('#4a90e2');
    }
  });
};

// Whether a LinkedIn PDF has given the person languages, certifications or honors
const hasCredentials = (contact) => [contact?.languages, contact?.certifications, contact?.honors]
  .some(list => Array.isArray(list) && list.length > 0);

const ContactDetailsDialog = ({
  selectedContactDetails,
  onClose,
  theme,
  onRefreshContact,
  onDataChanged,
  onOpenCompany
}) => {
  const [logoColor, setLogoColor] = useState('#4a90e2');
  const [shouldApplyGradient, setShouldApplyGradient] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const logoImageRef = useRef(null);

  // Full record from the API (includes every advisor who knows the person)
  const [detail, setDetail] = useState(null);

  const [editingField, setEditingField] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: '',
    phone: '',
    city: '',
    country: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);

  const contactId = selectedContactDetails?.id;

  useEffect(() => {
    setDetail(null);
    setEditingField(null);
    setUpdateError(null);
    if (!contactId) return undefined;

    let cancelled = false;
    contactApi.getContact(contactId)
      .then(fullContact => {
        if (cancelled) return;
        setDetail(fullContact);
        // Only the full record has them, so their section opens once it arrives
        if (hasCredentials(fullContact)) setExpandedSections(previous => ({ ...previous, credentials: true }));
      })
      .catch(error => console.error('Failed to load contact details:', error));
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    setIsSearchingLocations(true);
    try {
      setLocationSuggestions(await contactApi.searchLocations(query));
    } catch (error) {
      setLocationSuggestions([]);
    } finally {
      setIsSearchingLocations(false);
    }
  };

  const getSectionDataStatus = (contact) => {
    if (!contact) return {
      contactInfo: false,
      professionalSummary: false,
      workExperience: false,
      skills: false,
      credentials: false,
      education: false,
      history: true
    };

    return {
      contactInfo: !!(contact.email || contact.phone || contact.website),
      professionalSummary: !!(contact.bio || contact.headline || contact.summary),
      workExperience: !!(contact.work_experience && Array.isArray(contact.work_experience) && contact.work_experience.length > 0),
      skills: !!(contact.skills && Array.isArray(contact.skills) && contact.skills.length > 0),
      credentials: hasCredentials(contact),
      education: !!(contact.education && Array.isArray(contact.education) && contact.education.length > 0),
      history: true
    };
  };

  const [expandedSections, setExpandedSections] = useState({
    contactInfo: false,
    professionalSummary: false,
    workExperience: false,
    skills: false,
    credentials: false,
    education: false,
    history: true
  });
  // Bumped after an edit, so the history shows it
  const [historyVersion, setHistoryVersion] = useState(0);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match the CSS animation duration
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && selectedContactDetails && !editingField) {
        handleClose();
      }
    };

    if (selectedContactDetails) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedContactDetails, handleClose, editingField]);

  useEffect(() => {
    if (selectedContactDetails) {
      const contact = normalizeContactData(selectedContactDetails);
      const sectionStatus = getSectionDataStatus(contact);
      setExpandedSections({
        contactInfo: sectionStatus.contactInfo,
        professionalSummary: sectionStatus.professionalSummary,
        workExperience: sectionStatus.workExperience,
        skills: sectionStatus.skills,
        credentials: sectionStatus.credentials,
        education: sectionStatus.education,
        history: true
      });
    }
  }, [selectedContactDetails]);

  const handleLogoLoad = (imgElement) => {
    setTimeout(async () => {
      setLogoColor(await sampleColorFromImage(imgElement));
    }, 50);
  };

  useEffect(() => {
    if (selectedContactDetails) {
      setIsDialogVisible(true);

      const timer = setTimeout(() => {
        setShouldApplyGradient(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setIsDialogVisible(false);
    }
  }, [selectedContactDetails]);

  useEffect(() => {
    setLogoColor('#4a90e2');
    setShouldApplyGradient(false);
    setIsClosing(false);
    if (!selectedContactDetails) {
      setIsDialogVisible(false);
    }
  }, [selectedContactDetails]);

  if (!selectedContactDetails) return null;

  const contact = detail || normalizeContactData(selectedContactDetails);
  const advisors = contactAdvisors(contact);
  // Target accounts that list this person, and what is known about reaching them
  const outreach = Array.isArray(contact.outreach) ? contact.outreach : [];
  const emailStatus = outreach.find(item => item.email_status)?.email_status;
  const phoneNote = outreach.find(item => item.phone_note)?.phone_note;
  const doNotCall = outreach.some(item => item.do_not_call);
  const companyName = contact.company_name || contact.company_detail?.name;
  const industry = contact.company_detail?.industry || contact.industry;
  const location = contact.location_text || [contact.city_name, contact.country_name].filter(Boolean).join(', ');
  const profileUrl = contact.linkedin_url || contact.url;
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Name Not Available';

  const startEditing = (field) => {
    setEditingField(field);
    setUpdateError(null);

    if (field === 'email') {
      setEditFormData(prev => ({ ...prev, email: contact.email || '' }));
    } else if (field === 'phone') {
      setEditFormData(prev => ({ ...prev, phone: contact.phone || '' }));
    } else if (field === 'location') {
      setEditFormData(prev => ({
        ...prev,
        city: contact.city_name || '',
        country: contact.country_name || ''
      }));
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setUpdateError(null);
    setLocationSuggestions([]);
  };

  const saveChanges = async () => {
    if (!contactId) return;

    const changes = editingField === 'email' ? { email_address: editFormData.email.trim() }
      : editingField === 'phone' ? { phone: editFormData.phone.trim() }
      : { city: editFormData.city.trim(), country: editFormData.country.trim() };

    setIsUpdating(true);
    setUpdateError(null);

    try {
      setDetail(await contactApi.updateContact(contactId, changes));
      if (onRefreshContact) {
        onRefreshContact(contactId);
      }
      setHistoryVersion(version => version + 1);
      setEditingField(null);
      setLocationSuggestions([]);
    } catch (error) {
      setUpdateError(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // A LinkedIn PDF was saved to the person: show what it added, and refresh the view behind
  const handleProfileSaved = (updated) => {
    setDetail(updated);
    setExpandedSections(getSectionDataStatus(updated));
    setHistoryVersion(version => version + 1);
    if (onRefreshContact) onRefreshContact(contactId);
    if (onDataChanged) onDataChanged();
  };

  const handleResearched = async () => {
    handleProfileSaved(await contactApi.getContact(contactId));
  };

  const handleLocationSearchChange = (value, field) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'city') {
      searchLocations(value);
    }
  };

  const handleLocationSuggestionSelect = (suggestion) => {
    setEditFormData(prev => ({
      ...prev,
      city: suggestion.city,
      country: suggestion.country
    }));
    setLocationSuggestions([]);
  };

  const editorProps = {
    editFormData,
    setEditFormData,
    saveChanges,
    cancelEditing,
    isUpdating,
    error: updateError
  };

  const sectionProps = (sectionKey) => ({
    sectionKey,
    expanded: expandedSections[sectionKey],
    onToggle: toggleSection
  });

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const headerStyle = shouldApplyGradient && logoColor && logoColor !== '#4a90e2' ? {
    background: `linear-gradient(135deg, ${hexToRgba(logoColor, 0.4)} 0%, ${hexToRgba(logoColor, 0.2)} 50%, ${hexToRgba(logoColor, 0.1)} 100%)`,
    borderBottom: `1px solid ${hexToRgba(logoColor, 0.6)}`,
    transition: 'background 0.8s ease-in-out, border-bottom 0.8s ease-in-out'
  } : {
    transition: 'background 0.8s ease-in-out, border-bottom 0.8s ease-in-out'
  };

  const logoSource = {
    name: companyName,
    logo_uuid: contact.company_detail?.logo_uuid || contact.logo_uuid,
    logo_url: contact.company_detail?.logo_url || contact.company_logo_url
  };

  const avatarUrl = contact.profile_image_url || logoService.getFallbackLogoUrl(`${contact.first_name || 'Unknown'} ${contact.last_name || 'Contact'}`, { size: 100 });

  return (
    <aside className={`contact-dialog-overlay ${isDialogVisible ? 'visible' : ''} ${isClosing ? 'closing' : ''}`} aria-labelledby="dialog-title">
      <div
        className={`contact-dialog-fixed-header ${shouldApplyGradient && logoColor && logoColor !== '#4a90e2' ? 'gradient-ready' : ''} ${isDialogVisible ? 'slide-up' : ''} ${isClosing ? 'slide-down' : ''}`}
        style={headerStyle}
      >
        <div className="contact-dialog-header-content">
          <div className="contact-dialog-company-logo">
            <img
              ref={logoImageRef}
              src={logoService.getLogoWithFallback(logoSource, { size: 60, background: 'e5e7eb', color: '6b7280' })}
              alt={`${companyName || 'Company'} logo`}
              className="company-logo-image"
              onLoad={(e) => handleLogoLoad(e.target)}
              onError={(e) => {
                logoService.handleLogoError(e, { name: companyName }, { size: 60, background: 'e5e7eb', color: '6b7280' });
                e.target.onload = () => handleLogoLoad(e.target);
              }}
            />
          </div>

          <div id="dialog-title" className="contact-dialog-title">
            <h3 className="contact-name">{fullName}</h3>
            <div className="contact-title-company">
              <div className="contact-position">
                {contact.position?.name || contact.position_name || contact.title || 'Position Not Available'}
                {contact.title_as_written && (
                  <span className="contact-position-written" title="The title as its source wrote it"> · written as “{contact.title_as_written}”</span>
                )}
              </div>
              <div className="contact-company">
                {onOpenCompany && contact.company_detail?.id ? (
                  <button type="button" className="contact-company__link" onClick={() => onOpenCompany(contact.company_detail)}>
                    {companyName}
                  </button>
                ) : (companyName || 'Company Not Available')}
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="contact-dialog-close-btn"
          title="Close (Esc)"
          aria-label="Close dialog"
        >
          <Icon name="x" />
        </button>
      </div>

      <div className={`contact-dialog-scrollable-content ${isDialogVisible ? 'slide-up' : ''} ${isClosing ? 'slide-down' : ''}`}>
        <div className="contact-dialog-content">
        <div className="contact-dialog-profile-section">
          <div className="contact-dialog-image-container">
            {profileUrl ? (
              <a href={profileUrl} target="_blank" rel="noopener noreferrer" aria-label={`${fullName} on LinkedIn`}>
                <img
                  src={avatarUrl}
                  alt=""
                  className="contact-dialog-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = logoService.getFallbackLogoUrl(`${contact.first_name || 'Unknown'} ${contact.last_name || 'Contact'}`, { size: 100, background: '6c757d' });
                  }}
                />
                <div className="profile-click-overlay">
                  <span className="profile-click-text">View Profile</span>
                </div>
              </a>
            ) : (
              <img src={avatarUrl} alt="" className="contact-dialog-image" />
            )}
          </div>

          <div className="contact-dialog-basic-info">

            {editingField === 'location' ? (
              <InlineLocationEditor
                {...editorProps}
                locationSuggestions={locationSuggestions}
                isSearchingLocations={isSearchingLocations}
                onLocationSearchChange={handleLocationSearchChange}
                onLocationSuggestionSelect={handleLocationSuggestionSelect}
              />
            ) : (
              <div className="contact-dialog-field editable-field">
                <div className="field-content">
                  <Icon name="map-pin" /> <strong>Location:</strong>{' '}
                  {location || <span className="contact-dialog-muted">Not recorded</span>}
                </div>
                <EditButton label="location" onEdit={() => startEditing('location')} />
              </div>
            )}

            <div className="contact-dialog-field">
              <strong>Industry:</strong>{' '}
              {industry ? industryLabel(industry) : <span className="contact-dialog-muted">Not recorded</span>}
            </div>
            <div className="contact-dialog-field known-by">
              <strong>Known by:</strong>
              {advisors.length > 0 ? (
                <ul className="known-by-chips" aria-label="Advisors who know this person">
                  {advisors.map(advisor => (
                    <li key={advisor} className="known-by-chip">
                      <span className="known-by-dot" style={{ backgroundColor: getAdvisorColor(advisor) }} aria-hidden="true" />
                      {advisor}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="contact-dialog-muted"> {outreach.length ? 'No advisor connection yet' : 'Not recorded'}</span>
              )}
            </div>
            {outreach.map(item => (
              <div key={item.id} className="contact-dialog-outreach">
                <div className="outreach-title">
                  <Icon name="target" /> {item.role === 'to' ? 'Main contact' : 'CC contact'} for <strong>{item.account_name}</strong>
                </div>
                <div className="outreach-meta">
                  {item.icp_rank ? `${item.icp_rank}. ` : ''}{item.icp} · {targetAccountLabel(item)}
                </div>
                <div className="outreach-meta">{OUTREACH_STAGE_LABELS[item.outreach_stage] || item.status}</div>
              </div>
            ))}
            {contact.current_company && (
              <div className="contact-dialog-field now-at">
                <Icon name="briefcase" /> <strong>Now at:</strong> {contact.current_company.name}
                {contact.current_position && <> as {contact.current_position.name}</>}
                <span className="contact-dialog-muted"> (by a LinkedIn PDF; the list above still names their earlier company)</span>
              </div>
            )}
            {doNotCall && (
              <div className="outreach-warning" role="note">
                <Icon name="alert-triangle" /> Phone number is on a do-not-call list
              </div>
            )}
            {profileUrl && (
              <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="contact-dialog-profile-link">
                View LinkedIn profile <Icon name="external-link" />
              </a>
            )}
          </div>

        </div>

        <ProfilePdfUpload contactId={contactId} contactName={fullName} onSaved={handleProfileSaved} />

        <ResearchButton subjectType="contact" subjectId={contactId} onFinished={handleResearched} />

        <CollapsibleSection
          {...sectionProps('contactInfo')}
          title="Contact Information"
          icon="phone"
        >
          <div className="contact-dialog-info-grid">
            {editingField === 'email' ? (
              <InlineEmailEditor {...editorProps} />
            ) : (
              <div className="contact-dialog-field editable-field">
                <div className="field-content">
                  {contact.email ? (
                    <>
                      <Icon name="mail" /> <strong>Email:</strong>{' '}
                      <a href={`mailto:${contact.email}`} className="contact-dialog-link">
                        {contact.email}
                      </a>
                    </>
                  ) : emailStatus ? (
                    <>
                      <Icon name="mail" /> <strong>Email:</strong> <span className="contact-dialog-muted">{emailStatus}</span>
                    </>
                  ) : (
                    <div className="contact-dialog-placeholder-message">No email recorded</div>
                  )}
                </div>
                <EditButton label="email" onEdit={() => startEditing('email')} />
              </div>
            )}

            {editingField === 'phone' ? (
              <InlinePhoneEditor {...editorProps} />
            ) : (
              <div className="contact-dialog-field editable-field">
                <div className="field-content">
                  {contact.phone ? (
                    <>
                      <Icon name="phone" /> <strong>Phone:</strong>{' '}
                      <a href={`tel:${contact.phone}`} className="contact-dialog-link">
                        {contact.phone}
                      </a>
                      {phoneNote && <span className="contact-dialog-muted"> ({phoneNote})</span>}
                    </>
                  ) : phoneNote ? (
                    <>
                      <Icon name="phone" /> <strong>Phone:</strong> <span className="contact-dialog-muted">{phoneNote}</span>
                    </>
                  ) : (
                    <div className="contact-dialog-placeholder-message">No phone recorded</div>
                  )}
                </div>
                <EditButton label="phone" onEdit={() => startEditing('phone')} />
              </div>
            )}

            {contact.website && (
              <div className="contact-dialog-field">
                <Icon name="globe" /> <strong>Website:</strong>{' '}
                <a href={contact.website} target="_blank" rel="noopener noreferrer" className="contact-dialog-link">
                  {contact.website}
                </a>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {(contact.bio || contact.headline || contact.summary) && (
          <CollapsibleSection
            {...sectionProps('professionalSummary')}
            title="Professional Summary"
            icon="file-text"
          >
            <div className="contact-dialog-summary">
              {contact.headline && (
                <div className="contact-dialog-field">
                  <strong>Current Role:</strong> {contact.headline}
                </div>
              )}
              {contact.bio && (
                <div className="contact-dialog-field">
                  <strong>Bio:</strong> {contact.bio}
                </div>
              )}
              {contact.summary && contact.summary !== contact.bio && (
                <div className="contact-dialog-field">
                  <strong>Summary:</strong> {contact.summary}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        <CollapsibleSection
          {...sectionProps('workExperience')}
          title="Work Experience"
          icon="briefcase"
        >
          <div className="contact-dialog-work-experience">
            {contact.work_experience && Array.isArray(contact.work_experience) && contact.work_experience.length > 0 ? (
              contact.work_experience.map((exp, index) => (
                <div key={index} className="work-experience-item">
                  <div className="work-experience-logo">
                    {exp.company_logo_url ? (
                      <img
                        src={exp.company_logo_url}
                        alt={exp.company || ''}
                        className="work-experience-logo-img"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.textContent = exp.company ? exp.company.charAt(0).toUpperCase() : 'C';
                        }}
                      />
                    ) : (
                      exp.company ? exp.company.charAt(0).toUpperCase() : 'C'
                    )}
                  </div>
                  <div className="work-experience-details">
                    <div className="work-experience-title">{exp.title || exp.position || 'Position Not Specified'}</div>
                    <div className="work-experience-company">{exp.company || exp.company_name || 'Company Not Specified'}</div>
                    <div className="work-experience-duration">{experienceDates(exp)}</div>
                    {exp.location && (
                      <div className="work-experience-location"><Icon name="map-pin" /> {exp.location}</div>
                    )}
                    {exp.description && (
                      <div className="work-experience-description">{exp.description}</div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="contact-dialog-placeholder-message">
                No work history recorded yet.
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          {...sectionProps('skills')}
          title="Skills & Expertise"
          icon="target"
        >
          <div className="contact-dialog-skills">
            {contact.skills && Array.isArray(contact.skills) && contact.skills.length > 0 ? (
              <div className="skills-container">
                {contact.skills.map((skill, index) => (
                  <span key={index} className="skill-tag">
                    {skill.name || skill}
                  </span>
                ))}
              </div>
            ) : (
              <div className="contact-dialog-placeholder-message">
                No skills recorded yet.
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          {...sectionProps('credentials')}
          title="Languages, Certifications & Honors"
          icon="globe"
        >
          <div className="contact-dialog-credentials">
            {Array.isArray(contact.languages) && contact.languages.length > 0 && (
              <div className="credentials-group">
                <div className="credentials-label">Languages</div>
                <div className="skills-container">
                  {contact.languages.map((language, index) => (
                    <span key={index} className="skill-tag">
                      {language.name || language}
                      {language.proficiency && <span className="credentials-muted"> · {language.proficiency}</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(contact.certifications) && contact.certifications.length > 0 && (
              <div className="credentials-group">
                <div className="credentials-label">Certifications</div>
                <ul className="credentials-list">
                  {contact.certifications.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(contact.honors) && contact.honors.length > 0 && (
              <div className="credentials-group">
                <div className="credentials-label">Honors &amp; awards</div>
                <ul className="credentials-list">
                  {contact.honors.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              </div>
            )}
            {!hasCredentials(contact) && (
              <div className="contact-dialog-placeholder-message">
                None recorded yet. A LinkedIn PDF adds them.
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          {...sectionProps('education')}
          title="Education"
          icon="graduation-cap"
        >
          <div className="contact-dialog-education">
            {contact.education && Array.isArray(contact.education) && contact.education.length > 0 ? (
              contact.education.map((edu, index) => (
                <div key={index} className="education-item">
                  <div className="education-logo">
                    {edu.school_logo_url ? (
                      <img
                        src={edu.school_logo_url}
                        alt={edu.school || ''}
                        className="education-logo-img"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.textContent = edu.school ? edu.school.charAt(0).toUpperCase() : '';
                        }}
                      />
                    ) : edu.school ? (
                      edu.school.charAt(0).toUpperCase()
                    ) : (
                      <Icon name="graduation-cap" />
                    )}
                  </div>
                  <div className="education-details">
                    <div className="education-degree">{[edu.degree, edu.field_of_study].filter(Boolean).join(', ') || 'Degree Not Specified'}</div>
                    <div className="education-school">{edu.school || edu.institution || 'Institution Not Specified'}</div>
                    <div className="education-duration">
                      {edu.duration ||
                       (edu.start_year || edu.end_year ?
                         `${edu.start_year || 'Start Year Unknown'} - ${edu.end_year || 'End Year Unknown'}` :
                         'Duration Not Specified')}
                    </div>
                    {edu.description && (
                      <div className="education-description">{edu.description}</div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="contact-dialog-placeholder-message">
                No education recorded yet.
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          {...sectionProps('history')}
          title="History"
          icon="file-text"
        >
          <ContactHistory contactId={contactId} refreshKey={historyVersion} />
        </CollapsibleSection>
        </div>
      </div>
    </aside>
  );
};

export default ContactDetailsDialog;
